import React, { useState } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { PAGE_PERMISSIONS } from '../../utils/permissions';
import { useAddon } from '../../hooks/useAddon';
import { ADDONS } from '../../utils/addons';
import { usePlatformOwner } from '../../hooks/usePlatformOwner';

const Sidebar = ({ user, can, onShowSalesReps, mobileMenuOpen, setMobileMenuOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { enabled: aiAgentEnabled } = useAddon(ADDONS.AI_SALES_AGENT);
    const { isOwner, loading: ownerLoading, error: ownerError } = usePlatformOwner();
    const [reportsExpanded, setReportsExpanded] = useState(location.pathname.startsWith('/reports'));
    const [adminExpanded, setAdminExpanded] = useState(location.pathname.startsWith('/admin'));
    const [emailMarketingExpanded, setEmailMarketingExpanded] = useState(false);

    // Helper to check if the current path matches
    const isActive = (path) => location.pathname.startsWith(path);

    // derive user initials (prefer first + last name, then displayName/name/email)
    const initials = (() => {
        const name = (user?.firstName && user?.lastName)
            ? `${user.firstName} ${user.lastName}`
            : (user?.displayName || user?.name || user?.email || '');
        return name.split(/\s+/).filter(Boolean).slice(0,2).map(n => n[0].toUpperCase()).join('') || '?';
    })();

    // Close mobile menu when navigating
    const handleNavClick = () => {
        if (setMobileMenuOpen) {
            setMobileMenuOpen(false);
        }
    };

    return (
        <>
            {/* Mobile Backdrop Overlay */}
            {mobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar - Desktop: always visible, Mobile: slide-in drawer */}
            <div
                className={`
                    fixed md:static inset-y-0 left-0 z-50
                    flex flex-col w-64 min-w-[16rem] flex-shrink-0 box-border 
                    bg-white border-r overflow-y-auto
                    transition-transform duration-300 ease-in-out
                    ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
                `}
            >
                {/* Close button for mobile */}
                <div className="flex items-center justify-between h-16 border-b px-4">
                    <div className="flex items-center">
                        {/* T-shirt icon */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 64 64" fill="none" className="h-8 w-8 text-indigo-600">
                            <path
                                d="M8 16 L20 8 Q32 2 44 8 L56 16
                                   L52 24 L48 22 V54
                                   Q48 58 44 58 H20 Q16 58 16 54 V22
                                   L12 24 Z"
                                stroke="currentColor"
                                strokeWidth="3"
                                fill="none"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M24 12 Q32 8 40 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="none"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <span className="ml-2 font-bold text-lg">ApparelCRM</span>
                    </div>
                    
                    {/* Close button - Mobile only */}
                    <button
                        onClick={() => setMobileMenuOpen(false)}
                        className="md:hidden p-2 rounded-lg hover:bg-gray-100 focus:outline-none"
                        aria-label="Close menu"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Scrollable navigation area */}
                <div className="flex flex-col flex-grow overflow-y-auto p-4">
                    <nav className="flex flex-col space-y-1">
                        {/* Dashboard Page */}
                        {can(PAGE_PERMISSIONS.DASHBOARD) && (
                        <NavLink
                            to="/dashboard"
                            onClick={handleNavClick}
                            className={({ isActive }) => `flex items-center w-full text-left px-4 py-2 rounded-lg ${isActive ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                        >
                        {/* fixed-width icon column so label alignment never changes */}
                        <span className="w-6 flex-shrink-0 flex justify-center items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="#6366F1">
                                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                            </svg>
                        </span>
                        <span className="ml-3">Dashboard</span>
                    </NavLink>
                    )}
                    {/* Tasks */}
                    {can(PAGE_PERMISSIONS.TASKS) && (
                    <NavLink
                        to="/tasks"
                        onClick={handleNavClick}
                        className={({ isActive }) => `flex items-center w-full text-left px-4 py-2 rounded-lg ${isActive ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        {/* Colorful checklist icon */}
                        <span className="w-6 flex-shrink-0 flex justify-center items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24">
                                <rect x="5" y="4" width="14" height="16" rx="2" stroke="#10B981" strokeWidth="2" fill="#D1FAE5"/>
                                <path d="M8 9l2 2 4-4" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <line x1="9" y1="14" x2="16" y2="14" stroke="#10B981" strokeWidth="2" strokeLinecap="round"/>
                                <line x1="9" y1="17" x2="13" y2="17" stroke="#10B981" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </span>
                        <span className="ml-3">Tasks</span>
                    </NavLink>
                    )}
                    {/* Opportunities */}
                    {can(PAGE_PERMISSIONS.OPPORTUNITIES) && (
                    <NavLink
                        to="/opportunities"
                        onClick={handleNavClick}
                        className={({ isActive }) => `flex items-center w-full text-left px-4 py-2 rounded-lg ${isActive ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        <span className="w-6 flex-shrink-0 flex justify-center items-center">
                            {/* Funnel Icon */}
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                                <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" fill="#1dc6faff" stroke="#038cb6ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </span>
                        <span className="ml-3">Opportunities</span>
                    </NavLink>
                    )}
                    {/* Customers */}
                    {can(PAGE_PERMISSIONS.CUSTOMERS) && (
                    <NavLink
                        to="/customer"
                        onClick={handleNavClick}
                        className={({ isActive }) => `flex items-center w-full text-left px-4 py-2 rounded-lg ${isActive ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        <span className="w-6 flex-shrink-0 flex justify-center items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                                {/* Building base */}
                                <rect x="5" y="3" width="14" height="18" rx="0.5" fill="#93C5FD" stroke="#2563EB" strokeWidth="1.5"/>
                                {/* Windows row 1 */}
                                <rect x="7" y="5" width="2.5" height="2.5" rx="0.3" fill="#1E40AF"/>
                                <rect x="10.75" y="5" width="2.5" height="2.5" rx="0.3" fill="#1E40AF"/>
                                <rect x="14.5" y="5" width="2.5" height="2.5" rx="0.3" fill="#1E40AF"/>
                                {/* Windows row 2 */}
                                <rect x="7" y="9" width="2.5" height="2.5" rx="0.3" fill="#1E40AF"/>
                                <rect x="10.75" y="9" width="2.5" height="2.5" rx="0.3" fill="#1E40AF"/>
                                <rect x="14.5" y="9" width="2.5" height="2.5" rx="0.3" fill="#1E40AF"/>
                                {/* Windows row 3 */}
                                <rect x="7" y="13" width="2.5" height="2.5" rx="0.3" fill="#1E40AF"/>
                                <rect x="10.75" y="13" width="2.5" height="2.5" rx="0.3" fill="#1E40AF"/>
                                <rect x="14.5" y="13" width="2.5" height="2.5" rx="0.3" fill="#1E40AF"/>
                                {/* Door */}
                                <rect x="9.5" y="17" width="5" height="4" rx="0.3" fill="#1E3A8A"/>
                            </svg>
                        </span>
                        <span className="ml-3">Customers</span>
                    </NavLink>
                    )}
                    {/* Contacts */}
                    {can(PAGE_PERMISSIONS.CONTACTS) && (
                    <NavLink
                        to="/contacts"
                        onClick={handleNavClick}
                        className={({ isActive }) => `flex items-center w-full text-left px-4 py-2 rounded-lg ${isActive ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        <span className="w-6 flex-shrink-0 flex justify-center items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="7" r="4" fill="#86EFAC" stroke="#16A34A" strokeWidth="1.5"/>
                                <path d="M5 21c0-4 3-7 7-7s7 3 7 7" fill="#BBF7D0" stroke="#16A34A" strokeWidth="1.5"/>
                            </svg>
                        </span>
                        <span className="ml-3">Contacts</span>
                    </NavLink>
                    )}
                    {/* Sales Reps */}
                    {can(PAGE_PERMISSIONS.SALES_REPS) && (
                    <NavLink
                        to="/sales-reps"
                        onClick={handleNavClick}
                        className={({ isActive }) => `flex items-center w-full text-left px-4 py-2 rounded-lg ${isActive ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        <span className="w-6 flex-shrink-0 flex justify-center items-center">
                            {/* Handshake Icon - Two hands meeting */}
                            <span className="w-6 flex-shrink-0 flex justify-center items-center text-xl">💼</span>
                        </span>
                        <span className="ml-3">Sales Reps</span>
                    </NavLink>
                    )}
                    {/* Orders */}
                    {can(PAGE_PERMISSIONS.ORDERS) && (
                    <NavLink
                        to="/orders"
                        onClick={handleNavClick}
                        className={({ isActive }) => `flex items-center w-full text-left px-4 py-2 rounded-lg ${isActive ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        <span className="w-6 flex-shrink-0 flex justify-center items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24">
                                <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17" stroke="#038cb6ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                <circle cx="9" cy="19" r="2" fill="#038cb6ff"/>
                                <circle cx="17" cy="19" r="2" fill="#038cb6ff"/>
                            </svg>
                        </span>
                        <span className="ml-3">Orders</span>
                    </NavLink>
                    )}
                    {/* Calendar */}
                    {can(PAGE_PERMISSIONS.CALENDAR) && (
                    <NavLink
                        to="/calendar"
                        onClick={handleNavClick}
                        className={({ isActive }) => `flex items-center w-full text-left px-4 py-2 rounded-lg ${isActive ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        <span className="w-6 flex-shrink-0 flex justify-center items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                                <rect x="3" y="4" width="18" height="18" rx="2" stroke="#6366F1" strokeWidth="1.5" fill="#E0E7FF"/>
                                <path d="M3 9h18" stroke="#6366F1" strokeWidth="1.5"/>
                                <path d="M8 2v4M16 2v4" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round"/>
                                <rect x="7" y="12" width="3" height="3" rx="0.5" fill="#6366F1"/>
                                <rect x="14" y="12" width="3" height="3" rx="0.5" fill="#6366F1"/>
                                <rect x="7" y="16" width="3" height="3" rx="0.5" fill="#6366F1"/>
                            </svg>
                        </span>
                        <span className="ml-3">Calendar</span>
                    </NavLink>
                    )}
                    {/* Recommendations (AI Sales Agent add-on) */}
                    {aiAgentEnabled && can(PAGE_PERMISSIONS.RECOMMENDATIONS) && (
                    <NavLink
                        to="/recommendations"
                        onClick={handleNavClick}
                        className={({ isActive }) => `flex items-center w-full text-left px-4 py-2 rounded-lg ${isActive ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        <span className="w-6 flex-shrink-0 flex justify-center items-center text-lg" aria-hidden="true">✨</span>
                        <span className="ml-3">Recommendations</span>
                    </NavLink>
                    )}

                    {/* Separator line */}
                    {can(PAGE_PERMISSIONS.REPORTS) && (<>
                        <div className="my-3 border-t border-gray-300"></div>
                    {/* Reports - Expandable */}
                    <div>
                        <button
                            onClick={() => setReportsExpanded(!reportsExpanded)}
                            className={`flex items-center w-full text-left px-4 py-2 rounded-lg transition-colors ${
                                location.pathname.startsWith('/reports') 
                                    ? 'bg-indigo-600 text-white' 
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                        
                            <span className="w-6 flex-shrink-0 flex justify-center items-center">
                                {/* Colorful bar chart icon */}
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                                    <rect x="4" y="14" width="4" height="7" rx="1" fill="#60A5FA" stroke="#2563EB" strokeWidth="1"/>
                                    <rect x="10" y="8" width="4" height="13" rx="1" fill="#34D399" stroke="#059669" strokeWidth="1"/>
                                    <rect x="16" y="4" width="4" height="17" rx="1" fill="#F472B6" stroke="#DB2777" strokeWidth="1"/>
                                </svg>
                            </span>
                            <span className="ml-3 flex-grow">Reports</span>
                            <svg 
                                className={`w-4 h-4 transition-transform ${reportsExpanded ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        
                        {reportsExpanded && (
                            <div className="ml-6 mt-1 space-y-1 border-l-2 border-gray-200 pl-4">
                                <NavLink
                                    to="/reports"
                                    onClick={handleNavClick}
                                    className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                        isActive && location.pathname === '/reports'
                                            ? 'bg-indigo-100 text-indigo-700 font-medium' 
                                            : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                    end
                                >
                                    📊 Overview
                                </NavLink>
                                
                                <NavLink
                                    to="/reports/reps"
                                    onClick={handleNavClick}
                                    className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                        isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    👥 Sales Reps
                                </NavLink>                                
                                
                                <NavLink
                                    to="/reports/opportunities"
                                    onClick={handleNavClick}
                                    className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                        isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    📈 Opportunities
                                </NavLink>
                                
                                <NavLink
                                    to="/reports/customers"
                                    onClick={handleNavClick}
                                    className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                        isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    🏢 Customers
                                </NavLink>
                                
                                <NavLink
                                    to="/reports/orders"
                                    onClick={handleNavClick}
                                    className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                        isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    🛒 Orders
                                </NavLink>

                                <NavLink
                                    to="/reports/tasks"
                                    onClick={handleNavClick}
                                    className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                        isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    ✅ Tasks
                                </NavLink>

                                <NavLink
                                    to="/reports/inactivity"
                                    onClick={handleNavClick}
                                    className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                        isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    ⏱️ Customer Inactivity
                                </NavLink>

                                <NavLink
                                    to="/reports/comparison"
                                    onClick={handleNavClick}
                                    className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                        isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    🔀 Comparisons
                                </NavLink>
                            </div>
                        )}
                    </div>
                    </>)}

                    {/* Email Marketing Section - Permission-based visibility */}
                    {[PAGE_PERMISSIONS.EMAIL_CAMPAIGNS, PAGE_PERMISSIONS.EMAIL_TEMPLATES, PAGE_PERMISSIONS.EMAIL_SEGMENTS, PAGE_PERMISSIONS.EMAIL_ANALYTICS].some(p => can(p)) && (
                    <>
                        {/* Separator line */}
                        <div className="my-3 border-t border-gray-300"></div>
                        
                        {/* Email Marketing - Expandable */}
                        <div>
                            <button
                                onClick={() => setEmailMarketingExpanded(!emailMarketingExpanded)}
                                className={`flex items-center w-full text-left px-4 py-2 rounded-lg transition-colors ${
                                    location.pathname.startsWith('/email-') 
                                        ? 'bg-indigo-600 text-white' 
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                <span className="w-6 flex-shrink-0 flex justify-center items-center">📧</span>
                                <span className="ml-3 flex-grow">Email Marketing</span>
                                <svg 
                                    className={`w-4 h-4 transition-transform ${emailMarketingExpanded ? 'rotate-180' : ''}`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            
                            {emailMarketingExpanded && (
                                <div className="ml-6 mt-1 space-y-1 border-l-2 border-gray-200 pl-4">
                                    <NavLink
                                        to="/email-campaigns"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        📨 Campaigns
                                    </NavLink>
                                    
                                    <NavLink
                                        to="/email-templates"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        🎨 Templates
                                    </NavLink>
                                    
                                    <NavLink
                                        to="/email-segments"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        <span className="w-5 flex-shrink-0 flex justify-center items-center mr-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                                                <circle cx="9" cy="7" r="3.5" fill="#A78BFA" stroke="#7C3AED" strokeWidth="1"/>
                                                <circle cx="16" cy="7" r="3.5" fill="#60A5FA" stroke="#2563EB" strokeWidth="1"/>
                                                <circle cx="9" cy="16" r="3.5" fill="#F472B6" stroke="#DB2777" strokeWidth="1"/>
                                                <circle cx="16" cy="16" r="3.5" fill="#34D399" stroke="#059669" strokeWidth="1"/>
                                            </svg>
                                        </span>
                                        Segments
                                    </NavLink>
                                    
                                    <NavLink
                                        to="/email-marketing-analytics"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        <span className="w-5 flex-shrink-0 flex justify-center items-center mr-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="10" fill="none" stroke="#E5E7EB" strokeWidth="2"/>
                                                <path d="M12 12 L12 2 A10 10 0 0 1 22 12 Z" fill="#60A5FA" stroke="#2563EB" strokeWidth="1"/>
                                                <path d="M12 12 L22 12 A10 10 0 0 1 17 20 Z" fill="#34D399" stroke="#059669" strokeWidth="1"/>
                                                <path d="M12 12 L17 20 A10 10 0 0 1 7 20 Z" fill="#F472B6" stroke="#DB2777" strokeWidth="1"/>
                                                <path d="M12 12 L7 20 A10 10 0 0 1 12 2 Z" fill="#FCD34D" stroke="#F59E0B" strokeWidth="1"/>
                                            </svg>
                                        </span>
                                        Analytics
                                    </NavLink>
                                </div>
                            )}
                        </div>
                    </>
                    )}

                    {/* User Administration - Permission-based visibility */}
                    {[PAGE_PERMISSIONS.USER_ADMIN, PAGE_PERMISSIONS.ROLE_MANAGEMENT].some(p => can(p)) && (
                        <>
                        {/* Separator line */}
                        <div className="my-3 border-t border-gray-300"></div>
                        
                        {/* Administration - Expandable */}
                        <div>
                            <button
                                onClick={() => setAdminExpanded(!adminExpanded)}
                                className={`flex items-center w-full text-left px-4 py-2 rounded-lg transition-colors ${
                                    location.pathname.startsWith('/admin') 
                                        ? 'bg-indigo-600 text-white' 
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                <span className="w-6 flex-shrink-0 flex justify-center items-center">
                                    {/* Gear/settings icon */}
                                    ⚙️
                                </span>
                                <span className="ml-3 flex-grow">Administration</span>
                                <svg 
                                    className={`w-4 h-4 transition-transform ${adminExpanded ? 'rotate-180' : ''}`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            
                            {adminExpanded && (
                                <div className="ml-6 mt-1 space-y-1 border-l-2 border-gray-200 pl-4">
                                    <NavLink
                                        to="/admin"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive && location.pathname === '/admin'
                                                ? 'bg-indigo-100 text-indigo-700 font-medium' 
                                                : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                        end
                                    >
                                        📊 Overview
                                    </NavLink>
                                    
                                    <NavLink
                                        to="/admin/users"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        👥 Users
                                    </NavLink>                                
                                    
                                    <NavLink
                                        to="/admin/roles"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        🔑 Roles
                                    </NavLink>
                                    
                                    <NavLink
                                        to="/admin/notifications"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        🔔 Notifications
                                    </NavLink>
                                    
                                    <NavLink
                                        to="/admin/logs"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        📋 Logs
                                    </NavLink>
                                    
                                    <NavLink
                                        to="/admin/calendar-cleanup"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        🗓️ Calendar Cleanup
                                    </NavLink>
                                    
                                    <NavLink
                                        to="/admin/workflows"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        <span className="w-6 flex-shrink-0 flex justify-center items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                                                <circle cx="6" cy="6" r="3" fill="#C4B5FD" stroke="#7C3AED" strokeWidth="1.5"/>
                                                <circle cx="18" cy="6" r="3" fill="#BAE6FD" stroke="#0284C7" strokeWidth="1.5"/>
                                                <circle cx="12" cy="18" r="3" fill="#FED7AA" stroke="#EA580C" strokeWidth="1.5"/>
                                                <path d="M6 9v3h12V9" stroke="#6B7280" strokeWidth="1.5" fill="none"/>
                                                <path d="M12 12v3" stroke="#6B7280" strokeWidth="1.5"/>
                                            </svg>
                                        </span> Workflows
                                    </NavLink>

                                    <NavLink
                                        to="/admin/system-settings"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        🔧 System Settings
                                    </NavLink>

                                    {aiAgentEnabled && user?.role === 'admin' && (
                                        <NavLink
                                            to="/admin/quote-settings"
                                            onClick={handleNavClick}
                                            className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                                isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        >
                                            🧾 Quote Builder Settings
                                        </NavLink>
                                    )}

                                    <NavLink
                                        to="/admin/chatbot"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        🤖 Chatbot Admin
                                    </NavLink>

                                    <NavLink
                                        to="/admin/knowledge"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        📚 Knowledge Management
                                    </NavLink>

                                    <NavLink
                                        to="/admin/data-sync"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        🔄 Data Sync
                                    </NavLink>

                                    <NavLink
                                        to="/admin/achievements"
                                        onClick={handleNavClick}
                                        className={({ isActive }) => `flex items-center w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                                            isActive ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        🏆 Achievements
                                    </NavLink>
                                </div>
                            )}
                        </div>
                        </>
                    )}
                    {isOwner && !ownerLoading && !ownerError && (
                        <NavLink to="/platform-controls" onClick={handleNavClick}
                            className={({ isActive }) => `flex items-center w-full text-left px-4 py-2 rounded-lg ${isActive ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                            Platform Controls
                        </NavLink>
                    )}
                    </nav>
                </div>
            </div>
        </>
    );
};

export default Sidebar;